import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { PRIVACY_POLICY, CONTACT_INFO } from '../config/content';
import styles from './PrivacyPolicy.module.css';
import SEO from '../components/SEO';

const Reveal: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const PrivacyPolicy: React.FC = () => {
  return (
    <main className={styles.page}>
      <SEO path="/privacy-policy" />
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* Hero */}
      <div className={styles.heroWrapper}>
        <div className={styles.hero}>
          <motion.div
            className={styles.heroInner}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
          >
            <div className={styles.heroBadge}>
              <div className={styles.badgeDot} />
              <span>{PRIVACY_POLICY.hero.label}</span>
            </div>
            <h1>{PRIVACY_POLICY.hero.title}</h1>
            <p className={styles.heroSub}>{PRIVACY_POLICY.hero.subtitle}</p>
          </motion.div>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* Content */}
      <section className={styles.contentSection}>
        <div className={styles.contentContainer}>
          <Reveal>
            <p className={styles.lastUpdated}>Last updated: {PRIVACY_POLICY.lastUpdated}</p>
          </Reveal>

          {PRIVACY_POLICY.sections.map((section, i) => (
            <Reveal key={i} className={styles.policyBlock}>
              <h2 className={styles.sectionTitle}>{section.title}</h2>
              {section.content.split('\n\n').map((para, j) => (
                <p key={j} className={styles.paragraph}>
                  {para}
                </p>
              ))}
              {'list' in section && section.list && (
                <ul className={styles.rightsList}>
                  {section.list.map((item, k) => (
                    <li key={k}>{item}</li>
                  ))}
                </ul>
              )}
              {'afterList' in section && section.afterList && (
                <>
                  {section.afterList.split('\n\n').map((para, j) => (
                    <p key={`after-${j}`} className={styles.paragraph}>
                      {para}
                    </p>
                  ))}
                </>
              )}
            </Reveal>
          ))}

          <Reveal className={styles.contactBox}>
            <h3>Questions about this policy?</h3>
            <p>
              Reach out to us at{' '}
              <a href={CONTACT_INFO.emailHref}>{CONTACT_INFO.email}</a> or call{' '}
              <a href={CONTACT_INFO.phoneHref}>{CONTACT_INFO.phone}</a>.
            </p>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
